import React from 'react';
import Shield from 'lucide-react/dist/esm/icons/shield.js';
import Target from 'lucide-react/dist/esm/icons/target.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import { charityConfig } from '../../config/charity';
import InlineTranslationEdit from '../InlineTranslationEdit';
import { useContent } from '../../contexts/ContentContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';

export default function SurvivorShieldCharity({ editMode = false, handleTranslationUpdate }) {
  const { c } = useContent();
  const { siteDetails } = useSiteConfig() || {};
  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl;
  const brandName = siteDetails?.siteName || charityConfig.name;
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-blue-950 dark:via-purple-950 dark:to-pink-950 rounded-2xl shadow-lg border border-blue-200 dark:border-blue-800">
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-pink-400/20 to-purple-400/20 rounded-full blur-2xl"></div>

      <div className="relative p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg shadow-md">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-gray-900 dark:text-white"><InlineTranslationEdit translationKey="CharitySurvivorShieldTitle" value={c('CharitySurvivorShieldTitle', 'Standing Strong')} fallback="Standing Strong" onUpdate={handleTranslationUpdate} editMode={editMode} /></h3>
          </div>
          <div className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/40 dark:to-purple-900/40 rounded-full">
            <Target className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold text-blue-700 dark:text-blue-300"><InlineTranslationEdit translationKey="CharitySurvivorShieldChampion" value={c('CharitySurvivorShieldChampion', 'Champion')} fallback="Champion" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
          </div>
        </div>

        <div className="flex justify-center mb-4">
          <div className="relative">
            <svg
              viewBox="0 0 100 120"
              className="w-32 h-40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3B82F6" />
                  <stop offset="50%" stopColor="#8B5CF6" />
                  <stop offset="100%" stopColor="#EC4899" />
                </linearGradient>
                <linearGradient id="shieldInner" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#DBEAFE" />
                  <stop offset="100%" stopColor="#F3E8FF" />
                </linearGradient>
              </defs>
              <path
                d="M50 5 L95 25 L95 60 Q95 95 50 115 Q5 95 5 60 L5 25 Z"
                fill="url(#shieldGradient)"
                stroke="none"
              />
              <path
                d="M50 15 L85 32 L85 58 Q85 87 50 105 Q15 87 15 58 L15 32 Z"
                fill="url(#shieldInner)"
                className="dark:opacity-20"
              />
            </svg>

            <div className="absolute inset-0 flex items-center justify-center pt-2">
              <div className="w-16 h-16 rounded-full bg-white dark:bg-gray-800 shadow-lg flex items-center justify-center border-2 border-purple-300 dark:border-purple-600">
                <img
                  src={brandLogo}
                  alt={brandName}
                  className="w-12 h-12 object-contain rounded-full"
                />
              </div>
            </div>

            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
              <div className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-400 to-yellow-400 rounded-full shadow-lg">
                <Trophy className="w-3 h-3 text-amber-800" />
                <span className="text-xs font-bold text-amber-900"><InlineTranslationEdit translationKey="CharitySurvivorShieldChampionBadge" value={c('CharitySurvivorShieldChampionBadge', 'CHAMPION')} fallback="CHAMPION" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mb-4">
          <h4 className="font-bold text-gray-900 dark:text-white text-lg">{brandName}</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">{charityConfig.tagline}</p>
        </div>

        <div className="bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 dark:from-blue-900/30 dark:via-purple-900/30 dark:to-pink-900/30 rounded-xl p-4 border border-purple-200 dark:border-purple-700">
          <div className="flex items-start gap-2">
            <Heart className="w-5 h-5 text-pink-500 dark:text-pink-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              <InlineTranslationEdit translationKey="CharitySurvivorShieldImpact" value={c('CharitySurvivorShieldImpact', 'A cause worth fighting for.')} fallback="A cause worth fighting for." onUpdate={handleTranslationUpdate} editMode={editMode} />{' '}{charityConfig.impactStatement}
            </p>
          </div>
        </div>

        <div className="mt-4 text-center">
          <a
            href={charityConfig.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors group"
          >
            <InlineTranslationEdit translationKey="CharitySurvivorShieldCTA" value={c('CharitySurvivorShieldCTA', 'Join the fight')} fallback="Join the fight" onUpdate={handleTranslationUpdate} editMode={editMode} />
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  );
}
